import { useMemo } from 'react';

import { Box } from '@twilio-paste/core/box';
import { Menu, MenuButton, MenuItem, MenuSeparator, useMenuState } from '@twilio-paste/core/menu';
import { MoreIcon } from '@twilio-paste/icons/esm/MoreIcon';

import { AppChannel, AppModel, AppStatus } from '../../../../modules/Apps';
import { Action, Subject } from '../../../../modules/Permissions';
import { Can } from '../../../common/AbilityContext';

import { ActivateAppDialog } from './ActivateAppDialog';
import { DeactivateAppDialog } from './DeactivateAppDialog';
import { DeleteAppDialog } from './DeleteAppDialog';
import { useAppsTableActions } from './lib';

interface AppsTableActionsParams {
  app: AppModel;
}

export function AppsTableActions({ app }: AppsTableActionsParams) {
  const menu = useMenuState();
  const {
    handleCloseActivateDialog,
    handleCloseDeactivateDialog,
    handleOpenActivateDialog,
    handleOpenDeactivateDialog,
    handleCloseDeleteDialog,
    handleOpenDeleteDialog,
    isOpenActivateDialog,
    isOpenDeactivateDialog,
    isOpenDeleteDialog,
    openEditAppDrawer,
    handleNavigateToScopes,
    handleNavigateToTargets,
    openViewAppDrawer,
  } = useAppsTableActions(app);

  const isEnabled = app.status === AppStatus.Enabled;
  const showTargetsScopes = useMemo(
    () => app.appChannels.some(({ channel }) => channel === AppChannel.Sms),
    [app.appChannels],
  );

  return (
    <Box display="flex" justifyContent="center">
      <MenuButton {...menu} variant="reset" size="reset">
        <MoreIcon decorative={false} title="More options" />
      </MenuButton>
      <Menu {...menu} aria-label="Preferences">
        <Can I={Action.View} a={Subject.App}>
          <MenuItem {...menu} onClick={openViewAppDrawer(app)}>
            View
          </MenuItem>
        </Can>
        <Can I={Action.Edit} a={Subject.App}>
          <MenuItem {...menu} onClick={openEditAppDrawer(app)}>
            Edit
          </MenuItem>

          {!isEnabled && (
            <MenuItem {...menu} onClick={handleOpenActivateDialog}>
              Activate
            </MenuItem>
          )}
          {isEnabled && (
            <MenuItem {...menu} onClick={handleOpenDeactivateDialog}>
              Deactivate
            </MenuItem>
          )}
        </Can>
        {showTargetsScopes && (
          <>
            <MenuItem {...menu} onClick={handleNavigateToScopes}>
              Scopes
            </MenuItem>
            <MenuItem {...menu} onClick={handleNavigateToTargets}>
              Targets
            </MenuItem>
          </>
        )}
        <Can I={Action.Delete} a={Subject.App}>
          <MenuSeparator {...menu} />
          <MenuItem {...menu} onClick={handleOpenDeleteDialog} variant="destructive">
            Delete
          </MenuItem>
        </Can>
      </Menu>
      <DeactivateAppDialog
        app={app}
        handleClose={handleCloseDeactivateDialog}
        isOpen={isOpenDeactivateDialog}
      />
      <ActivateAppDialog
        app={app}
        handleClose={handleCloseActivateDialog}
        isOpen={isOpenActivateDialog}
      />{' '}
      <DeleteAppDialog
        app={app}
        handleClose={handleCloseDeleteDialog}
        isOpen={isOpenDeleteDialog}
      />
    </Box>
  );
}
